import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Download, Copy, CheckCircle, ExternalLink, Share2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MyQRPage() {
  const { qrId } = useParams();
  const navigate = useNavigate();
  
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Fetch QR data
  useEffect(() => {
    async function fetchQR() {
      try {
        const res = await fetch(`${API_URL}/api/qr/${qrId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('QR code not found');
          } else {
            setError('Failed to load QR code');
          }
          return;
        }
        const data = await res.json();
        setQrData(data);
      } catch (err) {
        setError('Failed to load QR code');
      } finally {
        setLoading(false);
      }
    }
    
    if (qrId) {
      fetchQR();
    }
  }, [qrId]);
  
  const fullUrl = `${window.location.origin}/tip/${qrId}`;
  
  // Copy URL to clipboard (with fallback for older browsers)
  const copyToClipboard = useCallback(async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        // Fallback for older browsers or insecure contexts
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      // Final fallback - prompt user to copy manually
      window.prompt('Copy this link:', fullUrl);
    }
  }, [fullUrl]);
  
  // Download QR code as PNG
  const downloadQRCode = useCallback(() => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    // High resolution for printing (1024x1024)
    const scale = 4;
    canvas.width = 256 * scale;
    canvas.height = 256 * scale;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Download
      const link = document.createElement('a');
      link.download = `tipme-qr-${qrId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [qrId]);
  
  // Share (if Web Share API available)
  const shareQR = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Tip Me',
          text: 'Send me a tip!',
          url: fullUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      copyToClipboard();
    }
  }, [fullUrl, copyToClipboard]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800/50 border-gray-700">
          <CardContent className="flex flex-col items-center py-12">
            <XCircle className="h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">QR Not Found</h2>
            <p className="text-gray-400 text-center mb-6">{error}</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/register-qr')}
              className="border-gray-600 text-gray-300"
            >
              Create a QR Code
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800/50 border-gray-700">
        <CardContent className="flex flex-col items-center py-8">
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="my-qr-title">
            Your TipMe QR Code
          </h1>
          
          {qrData?.label && (
            <p className="text-gray-400 text-sm mb-4">{qrData.label}</p>
          )}
          
          {/* QR Code */}
          <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
            <QRCodeSVG
              id="qr-code-svg"
              value={fullUrl}
              size={220}
              level="H"
              includeMargin={true}
              bgColor="#FFFFFF"
              fgColor="#000000"
            />
          </div>
          
          {/* Primary Actions */}
          <div className="flex gap-3 w-full mb-4">
            <Button
              onClick={downloadQRCode}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-qr-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            
            <Button
              onClick={shareQR}
              variant="outline"
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="share-qr-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
          
          {/* Tip URL */}
          <div className="w-full bg-gray-700/50 rounded-lg p-3 mb-4">
            <div className="text-xs text-gray-500 mb-1">Tip Link</div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-emerald-400 text-sm truncate flex-1" data-testid="tip-url">
                {fullUrl}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyToClipboard}
                className="text-gray-400 hover:text-white shrink-0"
                data-testid="copy-url-btn"
              >
                {copied ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          {/* Secondary Actions */}
          <div className="flex flex-col gap-2 w-full">
            <Button
              onClick={() => navigate(`/tip/${qrId}`)}
              variant="outline"
              className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="preview-tip-page-btn"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview Tip Page
            </Button>
            
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full text-gray-500"
            >
              Go Home
            </Button>
          </div>
          
          {/* Usage Tips */}
          <div className="mt-6 p-4 bg-gray-700/30 rounded-lg w-full">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">How to use</h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Print and display at events or meetups</li>
              <li>• Add to your business card</li>
              <li>• Share the link on social media</li>
              <li>• Display on screen during streams</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
